import $ from 'jquery'
import _ from 'lodash'
import * as Canvas from 'canvas'

import dialogStack from 'scoreboard/components/dialog-stack'
import mobile from 'scoreboard/components/mobile'
import layoutManager from 'scoreboard/components/layout-manager'
import constants from 'scoreboard/components/constants'
import deviceType from 'scoreboard/components/device-type'
import { isInDialog } from 'scoreboard/components/dialog'

import CanvasContainerView from '../canvas-container-view'
import duplicateWobs from './duplicate-wobs'
import i18n from 'scoreboard/components/i18n'
import WidgetUpdater from './widget-updater'
import Tooltip from '../../components/tooltip'
import { isV5, isV6 } from 'scoreboard/components/version'

const EditableCanvasContainerView = CanvasContainerView.extend({
	events: {
		'end:dragging .canvas': 'onEndDragging'
	},

	initialize (options) {
		CanvasContainerView.prototype.initialize.apply(this, arguments)
		this.toolbar = options.toolbar
		this.formView = options.formView
		this.scrollNotifier = options.scrollNotifier

		this.model.data.fetchOptions = this._fetchDataOptions.bind(this)
	},

	render () {
		CanvasContainerView.prototype.render.apply(this, arguments)

		_.values(this.canvas.views).forEach(wobView => {
			wobView.view.widgetUpdater = this.widgetUpdater
		})

		this.addScrollNotifierListeners()

		return this
	},

	addScrollNotifierListeners () {
		// Note: can't use View.events because the scrollNotifier is not a
		// descendent of the canvas container
		this.scrollNotifier.find('.icon-dashboard-triangle')
			.on('click', e => this.toggleDashboardScrollNotifier(e))
			.on('touchstart', e => this.toggleDashboardScrollNotifier(e))
			.on('mouseenter', e => this.toggleDashboardScrollNotifier(e))
			.on('mouseleave', () => this.hideDashboardScrollNotifier())
	},

	createCanvas () {
		var canvas = new Canvas.EditableCanvas({
			collection: this.wobs,
			minHeight: this.options.minHeight,
			minWidth: this.options.minWidth,
			duplicate: duplicateWobs.factory({ maestro: this.maestro }),
			ignoreCopyPaste: () => this.ignoreCopyPaste()
		})

		canvas.setWobComparator((a, b) => {
			return WOB_SORT_ORDER[a.widget.get('type')]
				- WOB_SORT_ORDER[b.widget.get('type')]
		})

		canvas.use(Canvas.features.edges)
		canvas.use(Canvas.features.draggable)
		canvas.use(Canvas.features.droppable.bind(null, {
			uploadMessage: i18n.get('dashboards.uploadMessage')
		}))
		canvas.use(Canvas.features.marquee)
		canvas.use(Canvas.features.keybindings)

		this.listenTo(canvas, 'resize', boundingBox => {
			this.trigger('resize', boundingBox)
			this.resize(boundingBox)
		})

		// We don't want to listen for changes until everything is done rendering,
		// since some wobs (like speedometers) will resize themselves as they render.
		this.listenToOnce(canvas, 'ready', this._listenForChanges)

		this.toolbar.canvas = canvas

		this.widgetUpdater = new WidgetUpdater({
			canvas,
			fetchDataOptions: this._fetchDataOptions.bind(this),
			maestro: this.maestro
		})

		canvas.setSnapType(this.model.get('snapType')
			?? constants.DEFAULT_DASHBOARD_SNAP_TYPE)

		return canvas
	},

	_listenForChanges: function () {
		// Any time a wob is added, removed, or changed, we need to record the
		// change so that the save and cancel buttons can be updated
		//
		// Note: FormView.setDirty() was expensive to call repeatedly in 3.2.8,
		// but #22757 updated it so that the expensive work is only done once
		var setDirty = () => {
			this.isCanvasDirty = true
			this.formView.setDirty()
		}

		var toggleHistoryButtons = function () {
			this.toolbar.toggleUndo(this.canvas.history.hasUndo())
			this.toolbar.toggleRedo(this.canvas.history.hasRedo())
		}.bind(this)

		toggleHistoryButtons()

		var afterUndoRedo = function () {
			// update history button state (might not be able to undo/redo any more)
			toggleHistoryButtons()

			// #10236 All kinds of things could have changed that require fresh data,
			// including adding back a wob that was previously deleted
			let calendarPeriodId = this.wobs.options.getPeriodId()
			this.model.data.fetch({ calendarPeriodId })
				.then(() => {
					// #30860 Wait until the dashboard data has been fetched before
					// performing the dataset query, because some scenarios require
					// the dataset metadata in order to build the dataset query,
					// like in DatasetTableWidgetView.table()
					this.maestro.queryAll()
				})
		}.bind(this)

		// When the canvas gets ready to receive
		this.listenTo(this.canvas, 'start:droppable', () => {
			this.$('.dashboard-scroll-notifier').addClass('is-not-displayed')
			$('.dashboard-scroll-notifier-tooltip').addClass('is-not-displayed')
			this.positionUploadMessage()
		})
		this.listenTo(this.canvas, 'end:droppable', () => {
			this.$('.dashboard-scroll-notifier').removeClass('is-not-displayed')
			$('.dashboard-scroll-notifier-tooltip').removeClass('is-not-displayed')

			this.$('.scroll-container').removeClass('is-droppable')
		})

		this.listenTo(this.model, 'change:snapType', (dashboard, snapType) => {
			this.canvas.setSnapType(snapType)
		})

		this.listenTo(this.model, 'change:snapType change:background', setDirty)

		this.listenTo(this.canvas.history, 'undo redo', afterUndoRedo)
		this.listenTo(this.canvas.history, 'record', toggleHistoryButtons)

		var listenToWob = function (wob) {
			let setWobDirty = () => {
				let widgetView = this.getWidgetView(wob)
				if (widgetView?.ignoreChangeEvents()) {
					return
				}
				setDirty()
			}
			this.listenTo(wob, 'change', () => setWobDirty())
			// only listen for changes to properties/charts, because the wob listener
			// will handle move/resize stuff, and we don't want to mark dirty
			// on change:data
			this.listenTo(wob.widget,
				'change:properties change:chart change:report change:table',
				() => setWobDirty())

			process.nextTick(() => {
				// #24038 for new wobs, wob.get('connectors') is a plain Array until
				// wobs.js:set turns it into a Backbone.Collection. So we'll just
				// wait a tick and let the creation process finish before adding
				// the listener
				this.listenTo(wob.get('connectors'), 'change remove add',
					() => setWobDirty())
			})

			this.listenTo(wob.widget, 'record', function () {
				this.canvas.history.record()
			})
			this.listenTo(wob.widget, 'transaction', function (cb) {
				this.canvas.history.transaction(cb)
				// #58272 sometimes 'change' events are suppressed, but if we're
				// cutting an undo transaction, then the wob is definitely dirty
				setWobDirty()
			})

			this.listenTo(wob, 'change:widget', function (wob) {
				// wob.js internally clears out the wob model's 'widget' attribute
				// (ie wob.get('widget') or wob.attributes.widget) after creating
				// the widget model on wob.widget
				//
				// But when history.js does an undo/redo, it sets the 'widget'
				// attribute again, and doesn't affect the wob.widget model.
				//
				// So, when change:widget is fired by the undo/redo, we pass
				// along those changes to wob.widget and unset the 'widget'
				// attribute again
				//
				// #5081 Make sure the wob is done changing before we
				// start triggering widget changes
				process.nextTick(function () {
					wob.widget.set(wob.get('widget'))
					wob.unset('widget', { silent: true })
				})
			})

			// For some reason, in safari, clicking on a widget does not trigger the
			// $(document).click() needed to dismiss the scrollNotifierTooltip. Here,
			// we can detect wob selection and make sure the tooltip is put away properly.
			if (deviceType.safari) {
				this.listenTo(wob, 'change:selected', () => this.hideDashboardScrollNotifier())
			}
		}.bind(this)

		this.listenTo(this.wobs, 'remove', (wob) => {
			setDirty()
			this.maestro.delete(wob.widget)
		})

		this.listenTo(this.wobs, 'add', function (wob) {
			setDirty()
			listenToWob(wob)

			let widgetView = this.getWidgetView(wob)
			widgetView.widgetUpdater = this.widgetUpdater

			widgetView.$el
				// #57118 the is-new class only serves to trigger the
				// `newWidgetBounce` animation. Once the animation is done,
				// though, we want to remove it to avoid problems with impact-shape
				.one('animationend', () => widgetView.$el.removeClass('is-new'))
				.addClass('is-new')

			this.maestro.add(widgetView)
		})

		this.wobs.forEach(listenToWob)

		// When a widget is removed, also remove any other selected widgets
		this.listenTo(this.wobs, 'remove', multiAction(function (removed, wobs, opt, done) {
			var selectedIds = this.canvas.selection.wobs.map(function (wob) { return wob.cid })
			if (!_.includes(selectedIds, removed.cid)) {
				// if the the deleted wob wasn't part of the current selection,
				// then it was probably removed as a "side-effect" (ex. toggling
				// off Show Table on a chart deletes the data table wob).
				// In that case, don't delete the selected wobs
				done()
				return
			}

			_(this.canvas.selection.wobs)
				.map(function (selected) {
					return selected.widget
				})
				.filter(function (selected) {
					// don't try to re-delete the wob that was originally deleted
					return selected.cid !== removed.cid
						// and don't delete wobs that have a template
						&& !selected.wob.get('hasTemplate')
						// and don't delete locked wobs
						&& !selected.wob.get('locked')
				})
				.forEach(function (selected) {
					selected.destroy()
				})

			this.canvas.selection.clear()

			done()
		}.bind(this)))
	},

	getWidgetView (wob) {
		return _.find(this.canvas.views,
				view => view.model.cid === wob.cid)?.view
	},

	_fetchDataOptions: function (opt = {}) {
		return fetchDataOptions(_.extend({
			wobs: this.wobs,
			model: this.model,
			maestro: this.maestro
		}, opt))
	},

	/**
	 * Resizes the canvas to fit the bounding box
	 */
	resize (boundingBox) {
		boundingBox = boundingBox || this.canvas.boundingBox()

		let editableCanvas = this.$('.editable-canvas')

		// When we're in the mobile orientation, the screen content only takes
		// up as much vertical space in the browser as needed. In order to ensure
		// the canvas portion of the screen doesn't end ubruptly at the bottom of
		// the browser, if there is such space, keep the .editable-canvas tall enough to eliminate that
		// extra vertical space.
		if (mobile.isMobileOrientation()) {
			let nonFullscreenSpace= window.innerHeight
					- this.$('.display-pane-header').height()
					- this.$('.dashboard-data').height() - 2
			if (isV5()) {
				// toolbar height at the top of the view in fullscreen
				let fullscreenToolbarHeight = 101
				if (layoutManager.getLayout() == constants.LAYOUT_DASHBOARD_EDIT_FULLSCREEN) {
					editableCanvas.css('min-height', window.innerHeight - fullscreenToolbarHeight)
				} else {
					editableCanvas.css('min-height', nonFullscreenSpace)
				}
			}
			if (isV6()) {
				editableCanvas.css('min-height', nonFullscreenSpace)
			}
		} else {
			editableCanvas.css('min-height', '')
		}

		// If the .canvas has a higher width or height than the .canvas-container,
		// that means there are scrollbars, so show the .dashboard-scroll-notifier,
		// else, hide the .dashboard-scroll-notifier
		if (this.scrollNotifier.length > 0 && !this.model.isNew()) {
			if (boundingBox.maxX >= this.$('.canvas').width() ||
				boundingBox.maxY >= this.$('.canvas').height()) {
				this.scrollNotifier.removeClass('is-not-displayed')

				// If this is the first time we're showing the canvas and the .dashboard-scroll-notifier
				// is showing, and if there are wobs showing in the canvas, we want to display the tooltip.
				if (!this.hasShownEditView && this.canvas.collection.length > 0 && !this.isCanvasDirty) {
					this.$('.icon-dashboard-triangle').click()
					this.hasShownEditView = true
				}
			} else {
				this.scrollNotifier.addClass('is-not-displayed')
			}
		}

		this.toggleExpandMessage()

		// Without waiting till nextTick, the message doesn't resize all the way
		// upon switching to and from fullscreen
		process.nextTick(() => this.positionUploadMessage())
	},

	/**
	 * Hides or shows the "Dashboard automatically expands..." message based on whether it's covered vertically
	 * by the .canvas-edges.
	 */
	toggleExpandMessage() {
		var canvasEdgesHeight = this.$('.canvas-edges')[0].clientHeight
		var dashboardMessageTop = this.$el[0].clientHeight - 70
		// 70px = distance from the bottom of .scroll-container to the top of .dashboard-auto-expand-message text

		this.$('.dashboard-auto-expand-message').css({
			display: canvasEdgesHeight >= dashboardMessageTop ? 'none' : 'block'
		})
	},

	// If the .image-upload-message is present (when we're about to drag an image onto the canvas),
	// we need to make sure it's correctly fitting within the editable portion of the screen.
	positionUploadMessage: function() {
		var scrollContainer = this.$el
		var message = this.$('.image-upload-message')

		if (message.length <= 0) {
			return
		}
		scrollContainer.addClass('is-droppable')
		message.css({
			width: scrollContainer.innerWidth(),
			height: scrollContainer.innerHeight(),
			top: scrollContainer.scrollTop(),
			left: scrollContainer.scrollLeft()
		})
	},

	hideDashboardScrollNotifier: function() {
		this.scrollNotifierTooltip && this.scrollNotifierTooltip.remove()
		this.scrollNotifierTooltip = null
	},

	toggleDashboardScrollNotifier: function(e) {
		e.preventDefault()
		if (this.scrollNotifierTooltip) {
			this.hideDashboardScrollNotifier()
		} else {
			this.scrollNotifierTooltip = new Tooltip({
				// This tooltip doesn't actually appear when dialogs are present,
				// we're using .over-dialog to place it over top wob's #32972
				extraClass: 'dashboard-scroll-notifier-tooltip over-dialog',
				attachTo: e.target,
				content: i18n.get('dashboards.scrollNotifierMessage'),
				placement: 'above',
				arrow: 'right'
			}).render()

			this.scrollNotifierTooltip.on('remove', () => {
				this.scrollNotifierTooltip = null
			})
		}
	},

	onEndDragging: function (e, wobView) {
		// #26374 after moving a widget with its gear icon on the outside, it
		// may need to be flipped to the other side based on proximity to the
		// edge of the browser
		let controlView = _.get(wobView, 'controlView.widgetViewControl')
		if (controlView) {
			controlView.togglePositionOutside()
		}
	},

	/**
	 * Returns true if the canvas should ignore copy/paste events
	 */
	ignoreCopyPaste () {
		// #33002 by default, canvas won't do copy/paste if focus is on an input,
		// but any other focus (or no focus) will handle copy/paste events. However,
		// if we have a dialog open, it's unlikely that their intent is to duplicate
		// wobs on the dashboard
		//
		// #58571 dashboard editor itself could be in a dialog, so we need to
		// ignore that dialog when deciding if *another* dialog is open
		let dashboardInDialog = isInDialog(this.el)
		let dialogCount = dialogStack.dialogs.length

		let nonDashboardDialogOpen = dashboardInDialog
			? dialogCount > 1
			: dialogCount > 0

		return nonDashboardDialogOpen
	},

	getSaveData () {
		// Before we save, transform the wobs to `0,0` to remove extra left/top spacing. See #20655
		this.wobs.transform(0, 0)

		return {
			widgets: this.wobs.widgets(),
			snapType: this.model.get('snapType'),
			background: this.model.get('background')
		}
	}
})

// Secondary sort order when wobs have the same area. See #20080
const WOB_SORT_ORDER = {
	'image': 0,
	'dataset-filter': 1,
	'old-chart': 2,
	'initiative-chart': 3,
	'initiative-timeline': 4,
	'chart-table': 5,
	'dataset-table': 6,
	'report': 7,
	'notes': 8,
	'linear-gauge': 9,
	'speedometer': 10,
	'shape': 11,
	'perspective': 12,
	'dataset-value': 13,
	'text': 14
}

var multiActionRunning = false

/*
 * Ensures that the given action isn't re-run before it completes. Useful for things like
 * making sure that a 'change' listener that makes more changes isn't executed repeatedly.
 *
 * The provided "action" function will receive the same arguments it would have received
 * anyway, but with a "done" function added as a final argument. This done() function
 * should be called to indicate that the multi-action is done and won't be ingoring
 * calls anymore.
 */
function multiAction (action) {
	return function () {
		if (multiActionRunning) {
			return
		}

		multiActionRunning = true

		let args = _.toArray(arguments)
		args.push(() => multiActionRunning = false)

		action.apply(null, args)
	}
}

/**
 * Version of DashboardData.fetchDataOptions() that passes along the current
 * widget configuration.
 *
 * @param {Object} opt
 *
 * @param {Integer} opt.calendarPeriodId
 *
 * @param {Wobs} opt.wobs
 *
 * @param {Dashboard} opt.model
 *
 * @param {Maestro} opt.maestro
 *
 * @param {Array} [opt.wobCids] if provided, only send widget with those cids
 * to the server
 */
function fetchDataOptions (opt = {}) {
	let widgets = _(opt.wobs.widgets())
		.filter(w => {
			if (opt.wobCids) {
				// If wobCids provided, only send those widgets to the server.
				// Note that we use the model's cid instead of id in case the
				// widget's haven't been saved yet
				return _.includes(opt.wobCids, w.cid)
			} else {
				return true
			}
		})
		.map(node => {
			// Strip out the image attribute; it isn't necessary when retrieving data,
			// and can be quite large
			return _.omit(node, 'image')
		})
		.value()

	var url = 'api/dashboards/data?calendarPeriodId=' + opt.calendarPeriodId
	if (!opt.model.isNew()) {
		url += '&dashboardId=' + opt.model.id
	}

	return {
		type: 'POST',
		url: url,
		data: JSON.stringify({
			widgets: widgets,
			liveFilters: opt.maestro.liveFilters()
		})
	}
}

export default EditableCanvasContainerView
export { fetchDataOptions }

EditableCanvasContainerView.__filename__ = 'dashboards/edit/editable-canvas-container-view.js'
